import React from 'react';
import { AlertTriangle, Boxes, Info, Loader2, Package, RefreshCw, Users } from 'lucide-react';
import { Button } from '../Button';
import { useRememberedState } from '../../hooks/useRemembered';
import { MATERIAL_NICHT_EINGERICHTET_TEXT } from '../../services/material/materialService';
import {
    empfaengerKopfzeile,
    gueltigerReiter,
    offeneAnzahl,
    sichtbareReiter,
    type MaterialReiter,
} from '../../services/material/materialLogic';
import { ArtikelReiter } from './bereich/ArtikelReiter';
import { MeldungenReiter } from './bereich/MeldungenReiter';
import { useMaterial } from './useMaterial';

/**
 * Bereich „Material“ (docs/materialverwaltung-plan.md, Client 4.): Beschaffer
 * bearbeiten die Meldungen der QR-Wendekarten, wer verwalten darf, pflegt
 * Artikel und druckt Karten. Dünner Container — Daten in `useMaterial`,
 * Regeln in `materialLogic`, die Reiter unter `bereich/`.
 *
 * Die Rechte reicht App.tsx herein (`material.beschaffen` /
 * `material.verwalten`); die Datenbank prüft sie bei jedem Lesen (RLS) und
 * Schreiben (RPC) noch einmal. `nurLesen` blendet alle Knöpfe aus.
 */

export interface MaterialBereichProps {
    darfVerwalten: boolean;
    darfBeschaffen: boolean;
    nurLesen?: boolean;
    /** Zahl offener Meldungen nach jedem Laden — für das Badge im Menü. */
    onOffeneGeaendert?: (anzahl: number) => void;
}

const REITER: Record<MaterialReiter, { label: string; Icon: React.ElementType }> = {
    meldungen: { label: 'Meldungen', Icon: Package },
    artikel: { label: 'Artikel', Icon: Boxes },
};

const uhrzeit = (ms: number): string =>
    new Date(ms).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });

export const MaterialBereich: React.FC<MaterialBereichProps> = ({ darfVerwalten, darfBeschaffen, nurLesen = false, onOffeneGeaendert }) => {
    const material = useMaterial(onOffeneGeaendert);
    const { stand, nichtEingerichtet, laedt, ladeFehler, standMs } = material;

    const sichtbar = sichtbareReiter(darfBeschaffen, darfVerwalten);
    const [gemerkt, setReiter] = useRememberedState<MaterialReiter>('material.reiter', 'meldungen');
    const reiter = gueltigerReiter(gemerkt, sichtbar);

    // Bezug für „vor X Tagen“: der Zeitpunkt des letzten Ladens, nicht die
    // Uhr beim Rendern — nach jeder Änderung wird ohnehin neu geladen.
    const jetzt = new Date(standMs);
    const kopf = empfaengerKopfzeile(stand?.empfaenger);
    const offen = stand ? offeneAnzahl(stand.meldungen) : 0;

    return (
        <div data-glass-view="material" className="max-w-5xl mx-auto p-4 sm:p-6 animate-fade-in pb-24">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                <div className="min-w-0">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <Package className="w-6 h-6 text-indigo-600" /> Material
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Nachbestellkarten per QR-Code — Meldungen, Artikel und Kartendruck.</p>
                </div>
                <div className="flex items-center gap-1">
                    {(laedt || standMs > 0) && (
                        <span className="text-[11px] text-slate-400 tabular-nums" aria-live="polite">
                            {laedt ? 'Wird aktualisiert …' : `Stand ${uhrzeit(standMs)}`}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={material.neuLaden}
                        disabled={laedt}
                        aria-label="Aktualisieren"
                        title="Aktualisieren"
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:text-indigo-600 disabled:hover:bg-transparent"
                    >
                        <RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {sichtbar.length === 0 && (
                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                    Für den Material-Bereich fehlt das Recht „Materialmeldungen bearbeiten“ oder „Material verwalten“.
                </p>
            )}

            {nichtEingerichtet && (
                <p className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" /> {MATERIAL_NICHT_EINGERICHTET_TEXT}
                </p>
            )}

            {ladeFehler && (
                <div className="mb-4 flex flex-wrap items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3">
                    <p role="alert" className="text-xs text-red-700 flex-1 min-w-[12rem]">Nicht geladen: {ladeFehler}</p>
                    <Button variant="secondary" size="sm" onClick={material.neuLaden} disabled={laedt}>Erneut versuchen</Button>
                </div>
            )}

            {!stand && !nichtEingerichtet && !ladeFehler && sichtbar.length > 0 && (
                <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …</p>
            )}

            {stand && reiter && (
                <div className="space-y-5">
                    {kopf && (
                        <div
                            className={`flex items-start gap-3 p-3 rounded-xl border ${
                                kopf.warnung ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-teal-50 border-teal-100 text-teal-900'
                            }`}
                        >
                            {kopf.warnung
                                ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                                : <Users className="w-4 h-4 shrink-0 mt-0.5 text-teal-600" />}
                            <p className="text-xs leading-relaxed">{kopf.text}</p>
                        </div>
                    )}

                    {sichtbar.length > 1 && (
                        <div data-glass="reiter" className="flex flex-wrap gap-2" role="tablist" aria-label="Material">
                            {sichtbar.map(key => {
                                const { label, Icon } = REITER[key];
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        role="tab"
                                        aria-selected={reiter === key}
                                        onClick={() => setReiter(key)}
                                        className={`min-h-[44px] px-4 py-2 text-sm font-bold rounded-xl border transition-all flex items-center gap-2 cursor-pointer ${
                                            reiter === key
                                                ? 'bg-white border-indigo-200 text-indigo-600 shadow-sm'
                                                : 'bg-slate-50 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" /> {label}
                                        {key === 'meldungen' && offen > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border bg-amber-100 text-amber-800 border-amber-200">
                                                {offen}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {reiter === 'meldungen' && (
                        <MeldungenReiter
                            meldungen={stand.meldungen}
                            artikel={stand.artikel}
                            verlaufGekappt={stand.verlaufGekappt}
                            jetzt={jetzt}
                            nurLesen={nurLesen}
                            onStatus={material.statusSetzen}
                        />
                    )}
                    {reiter === 'artikel' && (
                        <ArtikelReiter
                            artikel={stand.artikel}
                            meldungen={stand.meldungen}
                            nurLesen={nurLesen}
                            onSpeichern={material.artikelSpeichern}
                            onTokenNeu={material.tokenErneuern}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
